import { HttpStatusCode } from '@angular/common/http';
import { Injectable, ErrorHandler, Inject } from '@angular/core';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { Capacitor } from '@capacitor/core';
import { TAB_IDENTITY_MISMATCH } from '@shared/signup/constants';

import { ConnectionAlertService } from '../connection-alert';

/**
 * A deploy replaces the hashed lazy-chunk files, so a tab that was already open
 * requests a chunk name that no longer exists and webpack throws ChunkLoadError.
 * The view never renders. One reload picks up the new index.html and fixes it.
 * A dropped connection throws the same error; the webpack runtime has already
 * retried the request by then (setupChunkLoadRetry), and a reload is the next try.
 *
 * Applied in every app, guest-facing included. A tab left open across a deploy
 * is running code that no longer matches what the server serves, so leaving it
 * alone is not the safe option — it is the one that quietly misbehaves. Skipped
 * only on Capacitor, where the webview serves bundled assets and a reload cannot
 * fetch anything newer.
 */
const CHUNK_RELOAD_KEY = 'chunkLoadReloadedAt';

/**
 * How long an automatic reload blocks the next one: 15 minutes. It must outlast
 * the slowest failure that can follow a reload: webpack gives up on a hanging chunk
 * only after its 120 s chunkLoadTimeout, three attempts plus the retry backoff come
 * to just over 6 minutes, and a window shorter than that could reopen before the
 * reloaded page has even failed — a reload loop.
 */
const CHUNK_RELOAD_WINDOW_MS = 900_000;

const CHUNK_LOAD_ALERT_ID = 'chunk-load-failed';

/** Says nothing about a new version: most of these are a dropped connection. */
const CHUNK_LOAD_ALERT =
  "Part of TablesReady couldn't load. Check your connection and refresh the page.";

/**
 * The alert shows once per page. Stencil reports a failed chunk once per element
 * waiting on it, hundreds in one session, and the fixed alert id only de-duplicates
 * while the toast is still on screen.
 */
let chunkLoadAlertShown = false;

/**
 * One window listener per page, however many injectors instantiate this handler
 * (the manager provides it at app level and again in a lazy module).
 */
let rejectionWatchInstalled = false;

interface ErrorLike {
  name?: string;
  message?: string;
  status?: number;
  rejection?: unknown;
  error?: { message?: unknown; error?: unknown; code?: unknown };
}

/** zone.js hands its unhandled-rejection hook an Error wrapping the real one. */
type RejectionHook = (report: unknown) => void;

/**
 * `window.Zone` is the Zone *class*, so it is a function, not a plain object — a
 * guard that only accepts objects would silently skip the hook below.
 */
function isObjectLike(value: unknown): value is object {
  return (typeof value === 'object' || typeof value === 'function') && value !== null;
}

function isRejectionHook(value: unknown): value is RejectionHook {
  return typeof value === 'function';
}

function isSymbolLookup(value: unknown): value is (symbolName: string) => string {
  return typeof value === 'function';
}

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  constructor(
    @Inject('LOG_ERRORS') readonly isLoggingEnabled: boolean,
    private readonly alertService: AlertsService,
    private readonly connectionAlertService: ConnectionAlertService,
  ) {
    super();
    this.watchUnhandledRejections();
  }

  handleError(error: any): void {
    console.log(String(error));
    if (this.isLoggingEnabled) {
      console.log(error);
    }

    if (this.isChunkLoadError(error)) {
      this.handleChunkLoadError();

      return;
    }

    this.reportError(error);
  }

  private reportError(error: ErrorLike): void {
    const isErrorMessage = error?.error?.message !== undefined;
    const isErrorError = error?.error?.error !== undefined;
    const isHttpError = error?.name === 'HttpErrorResponse';

    if (this.isStaleTabRejection(error)) {
      return;
    }

    if (isHttpError && error.status === 0 && !Capacitor.isNativePlatform()) {
      console.log('Network connection lost. Please check your internet connection.');
      this.connectionAlertService.show();
    } else if (isErrorMessage) {
      this.alertService.addError(String(error.error.message));
    } else if (isErrorError) {
      this.alertService.addError(String(error.error.error));
    } else {
      super.handleError(error);
    }
  }

  /** A stale tab's blocked request: the lock screen already explains it. */
  private isStaleTabRejection(error: ErrorLike): boolean {
    return (
      error?.name === 'HttpErrorResponse' &&
      error.status === Number(HttpStatusCode.Conflict) &&
      error.error?.code === TAB_IDENTITY_MISMATCH
    );
  }

  /**
   * Webpack's runtime sets `error.name = 'ChunkLoadError'` on the same object it
   * writes the "Loading chunk N failed." message to, so the name is always there on
   * a real one and the message is never consulted. Matching the message would also
   * match any error that merely quotes that phrase, and the cost of a false match
   * here is a guest losing a part-filled form to a reload they never asked for.
   * The Stencil case below is the one exception, and it matches a property name,
   * not prose.
   */
  private isChunkLoadError(error: unknown): boolean {
    const cause = this.unwrapRejection(error);

    return cause?.name === 'ChunkLoadError' || this.isStencilLazyLoadFailure(cause);
  }

  /**
   * The same stale-chunk failure, as Ionic reports it. Stencil lazy-loads every
   * `ion-*` component's chunk and, when that import rejects, swallows the rejection
   * in a console.error and carries on with `undefined` — so the first thing that
   * reaches this handler is `Cstr.isProxied` being read off undefined: a TypeError,
   * never a ChunkLoadError. In production every one of these stacks sits in the
   * previous release's bundle, i.e. a tab left open across a deploy.
   *
   * `isProxied` is a Stencil-internal property that no application code names, so a
   * TypeError mentioning it comes from Stencil's `initializeComponent` and, in
   * practice, from a chunk it could not load. It is a recovery heuristic rather than
   * a proof of deploy skew — a chunk that is unreachable for any other reason, or a
   * build missing the export, reads the same — but every such case leaves the view
   * unrendered, and the guard in `claimReloadAttempt` bounds the cost of a reload
   * that does not help to one refresh per 15 minutes, with the alert in between.
   */
  private isStencilLazyLoadFailure(error: ErrorLike | null): boolean {
    return error?.name === 'TypeError' && /\bisProxied\b/u.test(String(error.message ?? ''));
  }

  /**
   * Zone.js reports an unhandled rejection by wrapping the real error, so unwrap
   * to the thing that actually failed before judging it.
   *
   * Iterative with a seen-set rather than recursive: this runs inside the global
   * error handler, and a self-referential wrapper would otherwise overflow the
   * stack while handling an error — turning a reportable problem into a crash.
   */
  private unwrapRejection(error: unknown): ErrorLike | null {
    const seen = new Set<unknown>();
    let current = error;

    while (current !== null && typeof current === 'object' && !seen.has(current)) {
      seen.add(current);

      const candidate: ErrorLike = current;

      if (!candidate.rejection) {
        return candidate;
      }

      current = candidate.rejection;
    }

    return null;
  }

  /**
   * Stencil initialises Ionic components outside the Angular zone, so the rejection
   * from a failed lazy load never reaches handleError: zone.js reports it itself
   * ("Unhandled Promise rejection: … ; Zone: <root>"). Zone.js then means to
   * re-dispatch it as a window `unhandledrejection` event, but in the version we
   * ship that bridge is broken — it invokes each listener's task with the event in
   * place of the task and swallows the resulting TypeError — so a window listener
   * alone never hears a root-zone ZoneAwarePromise rejection. It is heard by the
   * hook zone.js calls first, `Zone[__symbol__('unhandledPromiseRejectionHandler')]`,
   * so that is wrapped here and the previous hook is called for everything not
   * claimed. The window listener stays for native promises, which zone.js does not
   * own. Only the stale-chunk cases are claimed on either path.
   */
  private watchUnhandledRejections(): void {
    if (rejectionWatchInstalled || typeof window === 'undefined') {
      return;
    }
    // Whatever the zone lookup does, the window listener must still be installed
    // and the constructor must not throw during bootstrap.
    try {
      this.watchZoneRejections(Reflect.get(window, 'Zone'));
    } catch (error) {
      console.error('could not hook zone.js unhandled rejections', error);
    }

    window.addEventListener('unhandledrejection', (rejectionEvent: PromiseRejectionEvent) => {
      if (!this.isChunkLoadError(rejectionEvent.reason)) {
        return;
      }

      rejectionEvent.preventDefault();
      this.handleChunkLoadError();
    });
    rejectionWatchInstalled = true;
  }

  private watchZoneRejections(zone: unknown): void {
    if (!isObjectLike(zone)) {
      return;
    }

    const lookup: unknown = Reflect.get(zone, '__symbol__');
    if (!isSymbolLookup(lookup)) {
      return;
    }

    const hookName = lookup('unhandledPromiseRejectionHandler');
    const previousHook: unknown = Reflect.get(zone, hookName);
    const hook: RejectionHook = (report) => {
      if (this.isChunkLoadError(report)) {
        this.handleChunkLoadError();

        return;
      }

      if (isRejectionHook(previousHook)) {
        previousHook(report);
      }
    };
    Reflect.set(zone, hookName, hook);
  }

  private handleChunkLoadError(): void {
    if (Capacitor.isNativePlatform() || !this.claimReloadAttempt()) {
      this.showChunkLoadAlert();

      return;
    }

    window.location.reload();
  }

  private showChunkLoadAlert(): void {
    if (chunkLoadAlertShown) {
      return;
    }

    chunkLoadAlertShown = true;
    this.alertService.add({
      id: CHUNK_LOAD_ALERT_ID,
      type: IAlertTypes.danger,
      heading: 'Error:',
      body: CHUNK_LOAD_ALERT,
      // No close button. Ionic draws a toast button with an ion-ripple-effect, and when
      // the ripple chunk is the one that failed the toast never finishes loading, so
      // the alert would never show. It still closes itself after the usual duration.
      dismissable: false,
    });
  }

  /**
   * Allows one reload per tab per CHUNK_RELOAD_WINDOW_MS, and only when
   * sessionStorage demonstrably works.
   *
   * One reload is all a stale chunk needs: it fetches the new index.html and the
   * new chunk names. If a chunk error comes back within the window, reloading did
   * not help — the chunk is unreachable for now — so the alert takes over. The
   * window outlasts the slowest chunk failure, so a reload that did not help
   * always meets a fresh timestamp and the tab cannot loop; once it has passed, a
   * later deploy can reload the tab again.
   *
   * Storage that throws, or that silently discards the write, cannot bound the
   * loop, so treat it as "already used" and fall through to the alert. A stored
   * value that is not a timestamp is treated the same way.
   */
  private claimReloadAttempt(): boolean {
    try {
      const lastReload = window.sessionStorage.getItem(CHUNK_RELOAD_KEY);
      const isWindowOver =
        lastReload === null || Date.now() - Number(lastReload) > CHUNK_RELOAD_WINDOW_MS;
      if (!isWindowOver) {
        return false;
      }

      window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));

      return window.sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
    } catch {
      return false;
    }
  }
}
